// Verifies subtitle snapshots, for the two layers Decision 2 of
// docs/plans/2026-08-13-verification-integration-closeout.md describes as
// structurally distinct:
//
//   - a BASE track is part of the sealed manifest — verifyBaseSubtitle
//     checks it against SubtitleRef.hash, the same root manifest verifyRoot
//     already checked against the chain's root_hash.
//   - an ADDENDUM track is added after sealing, on its own PDA, with its own
//     hash. Decision 1: "the API points, the chain proves." So
//     verifyAddendumSubtitle takes the expected hash as a *decoded on-chain
//     SubtitleTrack account*, never a string — there is no reachable call
//     site that can pass Publication.Addenda[i].Hash (our own database) by
//     accident, because that value isn't shaped like a SubtitleTrackAccount.
//     A caller must have actually read the chain (see useForumVideo.ts's
//     pattern for the wallet-free read) to produce one.
import { API_BASE_URL } from '../constants.js'
import { hexEqual, sha256Hex } from './hash.js'
import type { SubtitleTrackAccount } from '../solana/forum/index.js'
import type { SubtitleRef } from './verifyRoot.js'

export interface SubtitleVerified {
  ok: true
  bytes: ArrayBuffer
  /** The computed hash, lowercase hex. */
  hash: string
}

export interface SubtitleVerificationFailure {
  ok: false
  stage: 'subtitle'
  /** Same discriminant T13 established for root.json: a network problem
   * must never be reported (or acted on) as a mismatch. See verifyRoot.ts. */
  kind: 'network' | 'mismatch'
  detail: string
  /** Normalized (trimmed, lower-cased) language code — see the module-level
   * note on D11 below for why this is never the caller's raw casing. */
  language: string
  /** Present only when kind === 'mismatch'. */
  expectedHash?: string
  /** Present only when kind === 'mismatch'. */
  actualHash?: string
}

export type VerifySubtitleResult = SubtitleVerified | SubtitleVerificationFailure

// D11 (Discovered Issues Log): the chain's shadowing guard compares raw
// bytes, so "FR" and "fr" are different on-chain values, and that casing gap
// has already caused one real hole (closed in T5 by normalizing every stored
// language to trimmed lower case). This module compares and requests
// languages on that same normalized form for the same reason: two callers
// spelling a language differently must never be treated as verifying
// different things.
const normalizeLang = (lang: string): string => lang.trim().toLowerCase()

type FetchAndHashResult = { ok: true; bytes: ArrayBuffer; hash: string } | SubtitleVerificationFailure

async function fetchAndHash(url: string, language: string): Promise<FetchAndHashResult> {
  let bytes: ArrayBuffer
  try {
    const response = await fetch(url)
    if (!response.ok) {
      return {
        ok: false,
        stage: 'subtitle',
        kind: 'network',
        detail: `subtitle "${language}" request failed: HTTP ${response.status}`,
        language,
      }
    }
    // Hashed before anything decodes it — see hash.ts and verifyRoot.ts.
    bytes = await response.arrayBuffer()
  } catch (err) {
    return {
      ok: false,
      stage: 'subtitle',
      kind: 'network',
      detail: err instanceof Error ? err.message : 'Could not reach the API',
      language,
    }
  }
  const hash = await sha256Hex(bytes)
  return { ok: true, bytes, hash }
}

/**
 * Verifies a BASE subtitle snapshot — part of the sealed video — against the
 * root manifest's SubtitleRef.hash. Never throws into the render path.
 */
export async function verifyBaseSubtitle(videoId: string, ref: SubtitleRef): Promise<VerifySubtitleResult> {
  const language = normalizeLang(ref.language)
  const fetched = await fetchAndHash(`${API_BASE_URL}/videos/${videoId}/verification/subtitles/${language}`, language)
  if (!fetched.ok) return fetched

  if (!hexEqual(fetched.hash, ref.hash)) {
    return {
      ok: false,
      stage: 'subtitle',
      kind: 'mismatch',
      detail: `subtitle "${language}" does not match the hash sealed on chain`,
      language,
      expectedHash: ref.hash,
      actualHash: fetched.hash,
    }
  }

  return { ok: true, bytes: fetched.bytes, hash: fetched.hash }
}

const toHex = (bytes: number[] | Uint8Array): string =>
  Array.from(bytes)
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('')

/**
 * Verifies an ADDENDUM subtitle track — added after the video was sealed —
 * against its OWN on-chain SubtitleTrack.hash, never against anything the
 * API asserts (Decision 1). `onChainTrack` must be a decoded account read
 * off the validator; see useForumVideo.ts for the wallet-free read pattern
 * this is meant to be paired with.
 *
 * The route this fetches from (`GET .../verification/addenda/:lang`) is
 * being built concurrently by T7 and may not exist on the server yet — a
 * 404 surfaces as an ordinary `kind: 'network'` failure, same as any other
 * unreachable route.
 */
export async function verifyAddendumSubtitle(
  videoId: string,
  lang: string,
  onChainTrack: Pick<SubtitleTrackAccount, 'hash'>,
): Promise<VerifySubtitleResult> {
  const language = normalizeLang(lang)
  const fetched = await fetchAndHash(`${API_BASE_URL}/videos/${videoId}/verification/addenda/${language}`, language)
  if (!fetched.ok) return fetched

  const expectedHash = toHex(onChainTrack.hash)
  if (!hexEqual(fetched.hash, expectedHash)) {
    return {
      ok: false,
      stage: 'subtitle',
      kind: 'mismatch',
      detail: `addendum subtitle "${language}" does not match the hash on its own chain account`,
      language,
      expectedHash,
      actualHash: fetched.hash,
    }
  }

  return { ok: true, bytes: fetched.bytes, hash: fetched.hash }
}
